import { useEffect, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useZustandPersistHasHydrated } from '~/hooks/useZustandPersistHasHydrated';

export type UseWorkspaceAgentPanelSpacerEnterParams = {
  isExpanded: boolean;
  isSlotOpen: boolean;
  showSpacer: boolean;
};

export type UseWorkspaceAgentPanelSpacerEnterResult = {
  hasHydratedStore: boolean;
  shouldAnimateSlotEnter: boolean;
  shouldAnimateSpacerEnter: boolean;
};

/**
 * Skip 0→width enter when Zustand rehydrates an already-open dock. First paint uses
 * `dock: 'minimized'`; treating that as a close would still play the enter on rehydrate.
 *
 * Clip enter keys off a collapsed card, not a closed in-flow slot. `dock: 'float'` is expanded
 * with the spacer down; treating that as hidden would replay `animate-width-in` on the next dock
 * and wipe an already-visible card from 0. The enter flag is latched for the whole docked
 * expansion so spacer measure cannot drop `data-animate` mid width-in.
 */
export const useWorkspaceAgentPanelSpacerEnter = ({
  isExpanded,
  isSlotOpen,
  showSpacer,
}: UseWorkspaceAgentPanelSpacerEnterParams): UseWorkspaceAgentPanelSpacerEnterResult => {
  const { hasHydrated: hasHydratedStore } = useZustandPersistHasHydrated(useWorkspaceAgentPanelStore.persist);
  const hasSeenCollapsedPanelRef = useRef<boolean>();
  const hasSeenHiddenSpacerRef = useRef<boolean>();
  const shouldAnimateThisDockRef = useRef<boolean>();
  const wasExpandedRef = useRef<boolean>();
  const wasSlotOpenRef = useRef<boolean>();

  if (hasHydratedStore) {
    if (!isExpanded || !isSlotOpen) {
      shouldAnimateThisDockRef.current = false;
    } else if (wasExpandedRef.current !== true && hasSeenCollapsedPanelRef.current === true) {
      shouldAnimateThisDockRef.current = true;
    } else if (wasExpandedRef.current === true && wasSlotOpenRef.current !== true) {
      shouldAnimateThisDockRef.current = false;
    }
  }

  useIsomorphicLayoutEffect(() => {
    if (!hasHydratedStore) {
      return;
    }

    if (!isExpanded) {
      hasSeenCollapsedPanelRef.current = true;
    }

    wasExpandedRef.current = isExpanded;
    wasSlotOpenRef.current = isSlotOpen;
  }, [hasHydratedStore, isExpanded, isSlotOpen]);

  useEffect(() => {
    if (!hasHydratedStore || showSpacer) {
      return;
    }

    hasSeenHiddenSpacerRef.current = true;
  }, [hasHydratedStore, showSpacer]);

  return {
    hasHydratedStore,
    shouldAnimateSlotEnter: shouldAnimateThisDockRef.current === true,
    shouldAnimateSpacerEnter: hasSeenHiddenSpacerRef.current === true,
  };
};
